import API from "../../rest/api";
import { getApiErrorMessage } from "../../utils/getApiErrorMessage";
import {
  PulseAccountsResponse,
  PulseAnalysisResponse,
  PulseDeleteAccountResponse,
  PulseDeleteDocumentsResponse,
  PulsePasswordResponse,
  PulseRangeUploadInput,
  PulseTransactionConsentInput,
  PulseTransactionConsentResponse,
  PulseUploadResponse,
} from "./types";

export interface UploadBankStatementInput {
  file: File;
  accountNumber?: string;
  scheduleId?: number | null;
  passwordRequired?: boolean;
  rangeUpload?: PulseRangeUploadInput;
}

const request = async <T>(
  fallbackMessage: string,
  call: () => Promise<T>
): Promise<T> => {
  try {
    return await call();
  } catch (error) {
    throw new Error(getApiErrorMessage(error, fallbackMessage), {
      cause: error,
    });
  }
};

export const getBankStatementAccounts = () =>
  request("Unable to load bank statements.", async () => {
    const response = await API.get<PulseAccountsResponse>(
      "/pulse/bank/accounts"
    );
    return response.data;
  });

export const uploadBankStatement = ({
  file,
  accountNumber,
  scheduleId,
  passwordRequired,
  rangeUpload,
}: UploadBankStatementInput) =>
  request("The statement could not be uploaded.", async () => {
    const formData = new FormData();
    formData.append("files", file);
    formData.append("scheduleId", scheduleId == null ? "" : String(scheduleId));
    if (accountNumber) formData.append("accountNumber", accountNumber);
    if (passwordRequired) formData.append("passwordRequired", "true");
    if (rangeUpload) {
      formData.append("rangeErrorType", rangeUpload.errorType);
      formData.append("rangeStart", rangeUpload.rangeStart);
      formData.append("rangeEnd", rangeUpload.rangeEnd);
    }

    const response = await API.post<PulseUploadResponse>(
      "/pulse/bankStatements/documents",
      formData,
      { headers: { "Content-Type": "multipart/form-data" } }
    );
    return response.data;
  });

export const applyBankStatementPassword = (
  docId: number,
  password: string,
  signal?: AbortSignal
) =>
  request("The password could not be saved.", async () => {
    const response = await API.post<PulsePasswordResponse>(
      "/pulse/bankStatements/documents",
      { docId, password },
      { signal }
    );
    return response.data;
  });

export const sendBankStatementsForAnalysis = (docIds: number[]) =>
  request("Unable to submit statements for analysis.", async () => {
    const response = await API.post<PulseAnalysisResponse>(
      "/pulse/bankStatements/sendForAnalysis",
      { docIds }
    );
    return response.data;
  });

export const updateBankStatementTransactionConsent = (
  input: PulseTransactionConsentInput
) =>
  request("Unable to update the no-activity confirmation.", async () => {
    const response = await API.post<PulseTransactionConsentResponse>(
      "/pulse/bankStatements/transaction/consent",
      input
    );
    return response.data;
  });

export const deleteBankStatementDocuments = (docIds: number[]) =>
  request("These statements could not be removed.", async () => {
    const response = await API.post<PulseDeleteDocumentsResponse>(
      "/pulse/bankStatements/documents/delete",
      { docIds }
    );
    return response.data;
  });

export const fetchBankStatementDocument = (
  fileKey: string,
  signal?: AbortSignal
) =>
  request("This statement could not be opened.", async () => {
    const response = await API.post<Blob>(
      "/underwriting/uploadjob/fetchfile",
      { fileKey },
      { responseType: "blob", signal }
    );
    return response.data;
  });

export const deleteBankAccount = (pulseBankAccountId: number) =>
  request("The bank account could not be deleted.", async () => {
    const response = await API.post<PulseDeleteAccountResponse>(
      "/pulse/bank/accounts/delete",
      { pulseBankAccountId }
    );
    return response.data;
  });
